import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, AlertTriangle, Eye, Upload } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Loading from '../../common/Loading';
import { fieldAPI, attendeeAPI } from '../../../api/endpoints';
import { getActiveUniqueFields } from '../../../utils/fieldHelpers';
import { 
  autoMapFields, 
  validateMappings, 
  applyMappings, 
  getMappingStats
} from '../../../utils/fieldMapper';
import { useToastContext } from '../../../context/ToastContext';
import ImportPreviewModal from './ImportPreviewModal';

const FieldMappingModal = ({ isOpen, onClose, selectedEvent, parsedData, onComplete }) => {
  const [mappings, setMappings] = useState({});
  const [validation, setValidation] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [sendEmailNotifications, setSendEmailNotifications] = useState(true);
  const toast = useToastContext();

  // Fetch custom fields for the event (same as manual attendee creation)
  const { data: fieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ['fields', selectedEvent], // Use same key as manual creation
    queryFn: async () => {
      if (!selectedEvent) return { data: [] };
      const response = await fieldAPI.getAll(selectedEvent);
      return response.data;
    },
    enabled: !!selectedEvent && isOpen,
  });

  // Define system fields that shouldn't be mappable (used in multiple places)
  const systemFields = React.useMemo(() => [
    'id', 
    'event_id', 
    'event_name',
    'registration_date',
    'last_updated', 
    'created_at', 
    'updated_at',
    'badge_number',
    'qr_code',
    'check_in_status',
    'check_in_time',
    'badge_printed'
  ], []);

  const customFields = React.useMemo(() => {
    if (!fieldsData) return [];
    
    // Use same field processing as manual attendee creation
    const allFields = getActiveUniqueFields(fieldsData);
    
    // Filter out system/auto-generated fields that shouldn't be mappable
    const mappableFields = allFields.filter(field => 
      !systemFields.includes(field.field_name.toLowerCase())
    );
    
    console.log('🔍 Field filtering:', {
      total: allFields.length,
      mappable: mappableFields.length,
      filtered_out: allFields.length - mappableFields.length,
      mappable_fields: mappableFields.map(f => f.field_name)
    });
    
    return mappableFields;
  }, [fieldsData, systemFields]);

  // Filter CSV headers to exclude system fields
  const mappableHeaders = React.useMemo(() => {
    if (!parsedData?.headers) return [];
    
    const filtered = parsedData.headers.filter(header => {
      // Check if this header looks like a system field
      const headerLower = header.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const isSystemField = systemFields.some(sysField => 
        headerLower.includes(sysField) || 
        sysField.includes(headerLower) ||
        headerLower === sysField.replace(/_/g, '')
      );
      
      return !isSystemField;
    });
    
    console.log('🔍 CSV Header filtering:', {
      total: parsedData.headers.length,
      mappable: filtered.length,
      filtered_out: parsedData.headers.length - filtered.length,
      original_headers: parsedData.headers,
      mappable_headers: filtered
    });
    
    return filtered;
  }, [parsedData?.headers, systemFields]);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      console.log('📥 Field Mapping Modal opened');
      setMappings({});
      setValidation(null);
      setShowPreview(false);
      setSendEmailNotifications(true); // Reset to true by default
    }
  }, [isOpen]);

  // Auto-map fields when data is loaded
  useEffect(() => {
    if (parsedData && customFields.length > 0 && mappableHeaders.length > 0) {
      console.log('🔄 Auto-mapping fields...');
      console.log('CSV Headers (filtered):', mappableHeaders);
      console.log('Custom Fields:', customFields.map(f => ({ name: f.field_name, label: f.field_label })));
      
      const autoMappings = autoMapFields(mappableHeaders, customFields);
      console.log('🎯 Auto mappings:', autoMappings);
      
      setMappings(autoMappings);
    }
  }, [parsedData, customFields, mappableHeaders]);

  // Validate mappings when they change
  useEffect(() => {
    if (Object.keys(mappings).length > 0 && customFields.length > 0) {
      const validationResult = validateMappings(mappings, customFields);
      setValidation(validationResult);
    }
  }, [mappings, customFields]);

  const handleMappingChange = (csvColumn, customField) => {
    setMappings(prev => ({
      ...prev,
      [csvColumn]: customField
    }));
  };

  const handlePreview = () => {
    if (!validation?.isValid) {
      toast.error('Please fix mapping errors before previewing');
      return;
    }
    setShowPreview(true);
  };

  const handleImport = async (importData) => {
    setIsImporting(true);
    
    try {
      console.log('📤 Starting import...', {
        eventId: selectedEvent,
        rowCount: importData.length,
        sampleRow: importData[0]
      });

      // Validate import data (same checks as manual creation)
      if (!importData || importData.length === 0) {
        throw new Error('No data to import');
      }

      if (!selectedEvent) {
        throw new Error('Please select an event');
      }

      // Validate that we have the required fields mapped (same as manual creation)
      const requiredFields = customFields.filter(field => {
        if (field.field_name === 'full_name') return true; // Always required
        return field.is_active === true && field.is_required === true;
      });

      const mappedFieldNames = Object.values(mappings)
        .filter(field => field !== null && field !== undefined)
        .map(field => field.field_name);

      const missingRequired = requiredFields.filter(field => 
        !mappedFieldNames.includes(field.field_name)
      );

      if (missingRequired.length > 0) {
        throw new Error(`Missing required fields: ${missingRequired.map(f => f.field_label).join(', ')}`);
      }

      // Transform data for API
      const formData = new FormData();
      
      // Create properly structured attendee data
      const attendeesData = importData.map((row, index) => {
        const attendeeData = {};
        
        // Extract field values from the mapped data structure
        Object.entries(row).forEach(([fieldName, fieldData]) => {
          // Skip internal fields
          if (fieldName.startsWith('_')) return;
          
          if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
            attendeeData[fieldName] = fieldData.value;
          } else if (typeof fieldData === 'string') {
            attendeeData[fieldName] = fieldData;
          }
        });
        
        console.log(`Row ${index + 1} transformed:`, attendeeData);
        return attendeeData;
      });

      // Create CSV using original column names as headers (not field names)
      // Only include columns that have valid field mappings
      const csvColumns = Object.keys(mappings).filter(col => {
        const field = mappings[col];
        return field !== null && field !== undefined && field.field_name;
      });
      
      if (csvColumns.length === 0) {
        throw new Error('No fields are mapped. Please map at least one field before importing.');
      }
      
      const csvHeaders = csvColumns;
      
      const csvRows = attendeesData.map(attendee => {
        return csvColumns.map(csvColumn => {
          const mappedField = mappings[csvColumn];
          const value = mappedField && mappedField.field_name ? (attendee[mappedField.field_name] || '') : '';
          // Escape quotes and wrap in quotes if contains comma or quote
          const escaped = String(value).replace(/"/g, '""');
          return escaped.includes(',') || escaped.includes('"') || escaped.includes('\n') 
            ? `"${escaped}"` 
            : escaped;
        });
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      console.log('Generated CSV content:', csvContent);
      console.log('CSV Headers:', csvHeaders);
      console.log('Sample CSV Row:', csvRows[0]);

      const blob = new Blob([csvContent], { type: 'text/csv' });
      formData.append('file', blob, 'import.csv');
      
      // Create proper field mappings: CSV column -> field name
      // Only include valid mappings and add comprehensive validation
      let validMappings = {};
      
      try {
        // Process each mapping with validation
        Object.entries(mappings).forEach(([csvCol, field]) => {
          if (field && field.field_name && typeof field.field_name === 'string') {
            validMappings[csvCol] = field.field_name;
          }
        });
        
        console.log('🔍 Processing field mappings:', {
          original_mappings: mappings,
          valid_mappings: validMappings,
          mapping_count: Object.keys(validMappings).length
        });
        
        // Validate that we have at least some field mappings
        if (Object.keys(validMappings).length === 0) {
          throw new Error('No valid field mappings found. Please map at least one field before importing.');
        }

        // Add default mappings for required fields if missing
        const requiredFields = ['full_name', 'email'];
        const mappedFields = Object.values(validMappings);
        
        requiredFields.forEach(requiredField => {
          if (!mappedFields.includes(requiredField)) {
            // Try to find a suitable CSV column for this required field
            const suitableColumn = mappableHeaders.find(header => {
              const headerLower = header.toLowerCase();
              return (
                (requiredField === 'full_name' && (headerLower.includes('name') || headerLower.includes('full'))) ||
                (requiredField === 'email' && headerLower.includes('email'))
              );
            });
            
            if (suitableColumn && !validMappings[suitableColumn]) {
              validMappings[suitableColumn] = requiredField;
              console.log(`✅ Auto-mapped required field: ${suitableColumn} -> ${requiredField}`);
            }
          }
        });

        console.log('Field mappings sent to backend:', validMappings);
        console.log('Total mapped fields:', Object.keys(validMappings).length);
        
        // Ensure fieldMappings is properly formatted JSON
        const fieldMappingsJson = JSON.stringify(validMappings);
        if (!fieldMappingsJson || fieldMappingsJson === '{}' || fieldMappingsJson === 'null') {
          throw new Error('Field mappings could not be serialized properly.');
        }
        
        // Validate JSON can be parsed back
        try {
          const parsed = JSON.parse(fieldMappingsJson);
          if (typeof parsed !== 'object' || parsed === null) {
            throw new Error('Field mappings JSON is not a valid object');
          }
        } catch (parseError) {
          throw new Error(`Field mappings JSON is invalid: ${parseError.message}`);
        }
        
        formData.append('fieldMappings', fieldMappingsJson);
        
      } catch (mappingError) {
        console.error('❌ Field mapping processing error:', mappingError);
        throw new Error(`Field mapping error: ${mappingError.message}`);
      }

      // Add import options with validation
      try {
        const options = {
          skipDuplicates: Boolean(true),
          updateExisting: Boolean(false),
          validateRequired: Boolean(true),
          sendEmailNotifications: Boolean(sendEmailNotifications || false)
        };
        
        const optionsJson = JSON.stringify(options);
        
        // Validate options JSON
        try {
          const parsed = JSON.parse(optionsJson);
          if (typeof parsed !== 'object' || parsed === null) {
            throw new Error('Options JSON is not a valid object');
          }
        } catch (parseError) {
          throw new Error(`Options JSON is invalid: ${parseError.message}`);
        }
        
        formData.append('options', optionsJson);
        
        console.log('✅ Import options validated:', options);
        
      } catch (optionsError) {
        console.error('❌ Options processing error:', optionsError);
        throw new Error(`Options error: ${optionsError.message}`);
      }

      // Debug FormData contents before sending
      console.log('🚀 Sending import request to backend...');
      console.log('📋 FormData contents:');
      for (let [key, value] of formData.entries()) {
        if (key === 'file') {
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }
      
      const response = await attendeeAPI.bulkImport(selectedEvent, formData);
      
      console.log('✅ Import response received:', response.data);
      
      const { imported, errors, skipped, error_details, skipped_details, message } = response.data;
      
      // Build comprehensive result message
      let resultMessage = message;
      const warnings = [];
      
      // Add error details to warnings
      if (error_details && error_details.length > 0) {
        warnings.push(`${error_details.length} rows had errors:`);
        error_details.slice(0, 5).forEach(error => { // Limit to first 5 errors
          warnings.push(`Row ${error.row}: ${error.errors ? error.errors.join(', ') : error.error}`);
        });
        if (error_details.length > 5) {
          warnings.push(`... and ${error_details.length - 5} more errors`);
        }
      }
      
      // Add skipped details to warnings
      if (skipped_details && skipped_details.length > 0) {
        warnings.push(`${skipped_details.length} rows were skipped:`);
        skipped_details.slice(0, 5).forEach(skip => { // Limit to first 5 skips
          warnings.push(`Row ${skip.row}: ${skip.reason} (${skip.email || 'no email'})`);
        });
        if (skipped_details.length > 5) {
          warnings.push(`... and ${skipped_details.length - 5} more skipped`);
        }
      }

      // Show detailed results
      console.log('📊 Import Results:', {
        imported: imported || 0,
        errors: errors || 0,
        skipped: skipped || 0,
        totalProcessed: (imported || 0) + (errors || 0) + (skipped || 0)
      });
      
      onComplete({
        success: true,
        imported: imported || 0,
        errors: errors || 0,
        skipped: skipped || 0,
        warnings: warnings,
        message: resultMessage
      });

    } catch (error) {
      console.error('❌ Import failed:', error);
      
      // Extract detailed error information
      let errorMessage = 'Import failed';
      if (error.response) {
        console.error('📋 Error response details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
        
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.errors) {
          errorMessage = `Validation errors: ${JSON.stringify(error.response.data.errors)}`;
        } else {
          errorMessage = `HTTP ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        console.error('📋 Network error - no response received:', error.request);
        errorMessage = 'Network error - no response from server';
      } else {
        console.error('📋 Request setup error:', error.message);
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      onComplete({
        success: false,
        message: errorMessage,
        errors: error.response?.data?.error_details || []
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setMappings({});
    setValidation(null);
    setShowPreview(false);
    setSendEmailNotifications(true); // Reset to true by default
    onClose();
  };

  if (fieldsLoading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <Modal.Content>
          <Loading text="Loading custom fields..." />
        </Modal.Content>
      </Modal>
    );
  }

  if (!parsedData) {
    return null;
  }

  const stats = validation?.stats || getMappingStats(mappings, customFields);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl">
        <Modal.Header>
          <h2 className="text-xl font-semibold text-gray-900">Map CSV Fields</h2>
          <p className="text-sm text-gray-600 mt-1">
            Match your CSV columns to the event's custom fields
          </p>
        </Modal.Header>

        <Modal.Content>
          {/* Mapping Statistics */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.mappedColumns}</div>
              <div className="text-sm text-blue-800">Mapped Columns</div>
              <div className="text-xs text-blue-600">of {stats.totalColumns} total</div>
            </div>
            <div className={`border rounded-lg p-4 ${
              stats.mappedRequiredFields === stats.requiredFields 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className={`text-2xl font-bold ${
                stats.mappedRequiredFields === stats.requiredFields 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {stats.mappedRequiredFields}
              </div>
              <div className={`text-sm ${
                stats.mappedRequiredFields === stats.requiredFields 
                  ? 'text-green-800' 
                  : 'text-red-800'
              }`}>
                Required Fields
              </div>
              <div className={`text-xs ${
                stats.mappedRequiredFields === stats.requiredFields 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                of {stats.requiredFields} required
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-600">{stats.mappingPercentage}%</div>
              <div className="text-sm text-gray-800">Mapping Complete</div>
              <div className="text-xs text-gray-600">
                {stats.unmappedColumns} columns ignored
              </div>
            </div>
          </div>

          {/* Validation Messages */}
          {validation && (
            <div className="mb-6 space-y-2">
              {validation.errors.map((error, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error.message}</p>
                </div>
              ))}
              {validation.warnings.map((warning, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  <p className="text-sm text-yellow-700">{warning.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* Import Options */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-3">Import Options</h3>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmailNotifications}
                  onChange={(e) => setSendEmailNotifications(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-blue-800">Send email notifications to imported attendees</span>
              </label>
            </div>
          </div>

          {/* Field Mapping Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Field Mappings</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      CSV Column
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-12">
                      →
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Custom Field
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-20">
                      Sample
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {mappableHeaders.map((header, index) => {
                    const mappedField = mappings[header];
                    const sampleValue = parsedData.rows[0]?.[header] || '';
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{header}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={mappedField?.field_name || ''}
                            onChange={(e) => {
                              const selectedField = customFields.find(f => f.field_name === e.target.value);
                              handleMappingChange(header, selectedField || null);
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">Skip this column</option>
                            {customFields.map(field => (
                              <option key={field.field_name} value={field.field_name}>
                                {field.field_label}
                                {field.is_required ? ' (Required)' : ''}
                              </option>
                            ))}
                          </select>
                          {mappedField?.is_required && (
                            <div className="mt-1 text-xs text-red-600">Required field</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-xs text-gray-500 max-w-20 truncate" title={sampleValue}>
                            {sampleValue || '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Modal.Content>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handlePreview}
            disabled={!validation?.isValid}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Preview Import
          </Button>
          <Button
            onClick={() => {
              if (validation?.isValid) {
                const mappedData = applyMappings(parsedData.rows, mappings);
                handleImport(mappedData);
              }
            }}
            disabled={!validation?.isValid || isImporting}
            loading={isImporting}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Import {parsedData.totalRows} Attendees
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Preview Modal */}
      <ImportPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        parsedData={parsedData}
        mappings={mappings}
        customFields={customFields}
        onConfirmImport={() => {
          setShowPreview(false);
          const mappedData = applyMappings(parsedData.rows, mappings);
          handleImport(mappedData);
        }}
      />
    </>
  );
};

export default FieldMappingModal;
